import React, { useState, useMemo } from "react";
import { Table, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh, faDownload, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";

export default function DataGridTableAdapter({
    columns = [],
    data = [],
    onRefresh,
    title,
    striped = false,
    bordered = false,
    hover = true,
    responsive = true,
    maxBodyHeight,
    fixedColumns = [], // Array of column indices to make sticky
    headerHeight = 41, // Height of header in pixels
}) {
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"

    // Handle column header click for sorting
    const handleHeaderClick = (col, colIdx) => {
        if (col.sorting === false) return; // Skip if sorting is disabled

        if (sortField === colIdx) {
            // Toggle sort order if clicking same column
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            // Set new column and default to ascending
            setSortField(colIdx);
            setSortOrder(col.defaultSort === "desc" ? "desc" : "asc");
        }
    };

    // Calculate sticky positions for fixed columns
    const getColumnStickyStyle = (colIdx) => {
        if (!fixedColumns.includes(colIdx)) return {};

        let left = 0;
        for (let i = 0; i < colIdx; i++) {
            if (fixedColumns.includes(i)) {
                left += 100; // Approximate width, adjust as needed
            }
        }

        return {
            position: "sticky",
            left: `${left}px`,
            backgroundColor: "inherit",
            zIndex: 10,
        };
    };

    const rowIdMap = data.map((row, idx) => ({
        ...row,
        _table_id: row.id ?? row._id ?? idx,
        _row_index: idx,
        tableData: { id: idx },
    }));

    // Filter rows based on search term
    const filteredRows = useMemo(() => {
        let rows = rowIdMap;

        if (searchTerm.trim()) {
            const lowerSearch = searchTerm.toLowerCase();
            rows = rowIdMap.filter((row) =>
                columns.some((col) => {
                    const fieldValue = col.field ? row[col.field] : "";
                    if (!fieldValue) return false;
                    return String(fieldValue).toLowerCase().includes(lowerSearch);
                })
            );
        }

        // Apply sorting
        if (sortField !== null && sortField < columns.length) {
            const col = columns[sortField];
            if (col.sorting !== false && col.field) {
                rows = [...rows].sort((a, b) => {
                    const aVal = a[col.field];
                    const bVal = b[col.field];

                    // Handle numeric sorting
                    const aNum = parseFloat(aVal);
                    const bNum = parseFloat(bVal);

                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
                    }

                    // Handle string sorting
                    const aStr = String(aVal ?? "").toLowerCase();
                    const bStr = String(bVal ?? "").toLowerCase();

                    if (sortOrder === "asc") {
                        return aStr.localeCompare(bStr);
                    } else {
                        return bStr.localeCompare(aStr);
                    }
                });
            }
        }

        return rows;
    }, [searchTerm, rowIdMap, columns, sortField, sortOrder]);

    // Export to CSV
    const handleExport = () => {
        const headers = columns
            .filter((col) => col.title)
            .map((col) => col.title || col.headerName || col.field || "")
            .join(",");

        const rows = filteredRows.map((row) =>
            columns
                .filter((col) => col.title)
                .map((col) => {
                    const value = col.field ? row[col.field] : "";
                    return `"${String(value).replace(/"/g, '""')}"`;
                })
                .join(",")
        );

        const csv = [headers, ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "export.csv";
        link.click();
    };

    return (
        <div style={{ width: "100%" }}>
            <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: "16px", fontWeight: "500" }}>{title}</div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <Form.Control
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="sm"
                    />
                    {onRefresh && (
                        <Button
                            variant="link"
                            size="sm"
                            onClick={onRefresh}
                            title="Refresh"
                            style={{ padding: "4px 8px" }}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                        </Button>
                    )}
                    <Button
                        variant="link"
                        size="sm"
                        onClick={handleExport}
                        title="Export to CSV"
                        style={{ padding: "4px 8px" }}
                    >
                        <FontAwesomeIcon icon={faDownload} />
                    </Button>
                </div>
            </div>

            <div style={{
                width: "100%",
                overflowX: "auto",
                overflowY: maxBodyHeight ? "auto" : "visible",
                maxHeight: maxBodyHeight,
                borderRadius: "4px"
            }}>
                <Table
                    striped={striped}
                    bordered={bordered}
                    hover={hover}
                    responsive={responsive}
                    size="sm"
                    style={{ marginBottom: 0, tableLayout: "auto" }}
                >
                    <thead style={{ position: "sticky", top: 0, zIndex: 20 }}>
                        <tr>
                            {columns.map((col, colIdx) => {
                                const isSorted = sortField === colIdx;
                                const isSortable = col.sorting !== false;

                                return (
                                    <th
                                        key={colIdx}
                                        onClick={() => handleHeaderClick(col, colIdx)}
                                        style={{
                                            whiteSpace: "nowrap",
                                            color: "var(--aoe-text-main)",
                                            padding: "8px",
                                            textAlign: col.align || "left",
                                            minWidth: col.title ? "auto" : "25px",
                                            fontSize: "0.875rem",
                                            fontWeight: "500",
                                            backgroundColor: "var(--aoe-gold)",
                                            cursor: isSortable ? "pointer" : "default",
                                            userSelect: "none",
                                            ...getColumnStickyStyle(colIdx),
                                        }}
                                        title={isSortable ? "Click to sort" : ""}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                            <span>{col.title || col.headerName || col.field || ""}</span>
                                            {isSortable && (
                                                <span style={{ marginLeft: "4px", minWidth: "12px" }}>
                                                    {isSorted ? (
                                                        <FontAwesomeIcon
                                                            icon={sortOrder === "asc" ? faSortUp : faSortDown}
                                                            size="xs"
                                                        />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faSort} size="xs" opacity={0.5} />
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={columns.length || 1}
                                    className="text-center"
                                    style={{ backgroundColor: "var(--aoe-bg-panel-2)", padding: "16px", color: "#666" }}
                                >
                                    Nessun dato disponibile
                                </td>
                            </tr>
                        ) : (
                            filteredRows.map((rowData, rowIdx) => (
                                <tr
                                    key={rowData._table_id}
                                >
                                    {columns.map((col, colIdx) => {
                                        let cellContent;
                                        if (col.render) {
                                            cellContent = col.render(rowData);
                                        } else {
                                            const fieldValue = col.field ? rowData[col.field] : "";
                                            cellContent = fieldValue ?? "";
                                        }
                                        return (
                                            <td
                                                key={colIdx}
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    verticalAlign: "middle",
                                                    padding: "8px",
                                                    textAlign: col.align || "left",
                                                    backgroundColor: rowIdx % 2 === 1 ? "var(--aoe-bg-panel-2)" : "var(--aoe-bg-panel)",
                                                    color: "var(--aoe-text-main)",
                                                    ...getColumnStickyStyle(colIdx),
                                                }}
                                            >
                                                {cellContent}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>
        </div >
    );
}
